import { useState } from 'react';
import { t } from 'i18next';
import { SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';

import {
  useAllProgramListQuery,
  usePatientCareNotesQuery,
  useProvidersShortQuery,
  useUserListShortQuery,
} from '@common/cache';
import {
  ICareNotesTableEntries,
  ICareNotesTableFilters,
  NoteType,
} from '@views/pages/Patient/components/CareNotes/hooks/useCareNotesTable.ts';
import { ALL_OPTION } from '@common/types/constants.ts';
import { isEqual } from 'lodash';

const initialFilter: ICareNotesTableFilters = {
  sort_order: 'desc',
  sort_by: 'last_updated_at',
  page: 1,
  limit: 100,
  program_id: '',
  provider_id: '',
  user_id: '',
  note_type: '',
};
export const useCareNotes = (patient_id: string) => {
  const [filters, setFilters] = useState<ICareNotesTableFilters>({
    ...initialFilter,
  });

  const isInitialFilter = isEqual(filters, initialFilter);

  const notesTypeOptions = [
    ALL_OPTION,
    { value: 'Email', label: t('common.notesTypeOptions.email') },
    { value: 'Phone call', label: t('common.notesTypeOptions.phoneCall') },
    { value: 'Reflection', label: t('common.notesTypeOptions.reflection') },
    { value: 'Text', label: t('common.notesTypeOptions.text') },
    { value: 'Other', label: t('common.notesTypeOptions.other') },
  ];

  const { data = { care_notes: [], count: 0 }, isLoading } =
    usePatientCareNotesQuery(patient_id, filters);

  const entries: ICareNotesTableEntries[] = data.care_notes.map(note => ({
    id: note.id,
    program: note.program_name || '-',
    note_type: note.note_type,
    last_updated_at: note.last_updated_at
      ? dayjs(note.last_updated_at).format('MMM DD, YYYY')
      : '-',
    recorded_minutes: note.recorded_minutes,
    provider: note.provider_name,
    created_by_name: note.created_by_name,
    note: note.note,
    notable_call: note.notable_call,
  }));

  const { data: programOptions = [], isPending: isProgramPending } =
    useAllProgramListQuery();
  const { data: providerOptions = [], isPending: isProviderPending } =
    useProvidersShortQuery({});
  const { data: addedByOptions = [], isPending: isAddedByPending } =
    useUserListShortQuery({});

  const handleChangeFilterNoteType = (event: SelectChangeEvent<string>) => {
    setFilters({ ...filters, note_type: event.target.value as NoteType });
  };

  const handleChangeFilterProgram = (event: SelectChangeEvent) => {
    setFilters({ ...filters, program_id: event.target.value });
  };

  const handleChangeFilterProvider = (event: SelectChangeEvent) => {
    setFilters({ ...filters, provider_id: event.target.value });
  };

  const handleChangeFilterAddedBy = (
    newValue: { value: string; label: string } | null,
  ) => {
    if (newValue) {
      setFilters({ ...filters, user_id: newValue.value });
    } else {
      setFilters({ ...filters, user_id: ALL_OPTION.value });
    }
  };

  const handleResetFilter = () => {
    setFilters(initialFilter);
  };

  return {
    notesTypeOptions,
    programOptions: [
      ALL_OPTION,
      ...programOptions.map(p => ({ value: p.id, label: p.name })),
    ],
    providerOptions: [
      ALL_OPTION,
      ...providerOptions.map(p => ({ value: p.id, label: p.name })),
    ],
    addedByOptions: [
      ALL_OPTION,
      ...addedByOptions.map(user => ({
        value: user.id,
        label: user.name,
      })),
    ],
    handleChangeFilterNoteType,
    handleChangeFilterProgram,
    handleChangeFilterProvider,
    handleChangeFilterAddedBy,
    handleResetFilter,
    isFilterDisabled:
      !filters.note_type &&
      !filters.program_id &&
      !filters.user_id &&
      !filters.provider_id,
    filters,
    setFilters,
    entries,
    count: data.count,
    isLoading:
      isLoading || isProgramPending || isAddedByPending || isProviderPending,
    isInitialFilter,
  };
};
